import { styled } from '@mui/material/styles';
import { Box, Typography, Button, TextField, Select, TableCell, TableRow } from '@mui/material';

// ============================================
// Controls Row
// ============================================
export const ControlsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '16px',
});

export const PageSizeGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PageSizeLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#737373',
  whiteSpace: 'nowrap',
});

export const PageSizeSelect = styled(Select)({
  height: '48px',
  minWidth: '88px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  fontSize: '16px',
  color: '#737373',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
});

export const SearchGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const DateRangeGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const PresetSelect = styled(Select)({
  height: '48px',
  minWidth: '110px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  fontSize: '16px',
  color: '#737373',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d9d9d9',
  },
});

export const DateFieldWrapper = styled(Box)({
  width: '170px',
  '& .MuiOutlinedInput-root': {
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: '16px',
    color: '#737373',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
  },
});

export const DateDivider = styled(Typography)({
  fontSize: '16px',
  color: '#737373',
  padding: '0 4px',
});

export const SearchInput = styled(TextField)({
  width: '320px',
  '& .MuiOutlinedInput-root': {
    height: '48px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: '16px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#a6a6a6',
    opacity: 1,
  },
});

export const SearchButton = styled(Button)({
  height: '48px',
  width: '120px',
  padding: '12px 24px',
  backgroundColor: '#4ab3bc',
  borderRadius: '8px',
  fontSize: '20px',
  fontWeight: 600,
  color: '#fff',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#3da3ab',
  },
});

export const ChargeButton = styled(Button)({
  height: '48px',
  padding: '8px 16px',
  backgroundColor: '#fff',
  border: '1px solid #4ab3bc',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 600,
  color: '#4ab3bc',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#f0fafb',
  },
});

// ============================================
// Table
// ============================================
export const TableWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'auto',
});

export const HeaderCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'fixedWidth',
})(({ fixedWidth }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
  textAlign: 'center',
  padding: '12px',
  backgroundColor: '#f2f2f2',
  borderBottom: '1px solid #d9d9d9',
  whiteSpace: 'nowrap',
  ...(fixedWidth && { width: fixedWidth, minWidth: fixedWidth }),
  '&:first-of-type': {
    borderTopLeftRadius: '8px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '8px',
  },
}));

export const BodyCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'fixedWidth' && prop !== 'align',
})(({ fixedWidth, align = 'center' }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  textAlign: align,
  padding: '16px 12px',
  borderBottom: '1px solid #d9d9d9',
  whiteSpace: 'pre-line',
  verticalAlign: 'middle',
  ...(fixedWidth && { width: fixedWidth, minWidth: fixedWidth }),
}));

export const DataRow = styled(TableRow)({
  backgroundColor: '#fff',
});

export const ContentCell = styled(BodyCell)({
  textAlign: 'left',
  whiteSpace: 'normal',
  lineHeight: 1.4,
  minWidth: '280px',
});

export const StatusText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => {
  let color = '#737373';
  if (status === 'SUCCESS' || status === 'SENT' || status === 'COMPLETED') color = '#55c982';
  else if (status === 'FAIL' || status === 'FAILED' || status === 'ERROR') color = '#ee735a';
  return {
    fontSize: '16px',
    fontWeight: 500,
    color,
  };
});

export const AttachedImage = styled('img')({
  width: '53px',
  height: '80px',
  objectFit: 'cover',
  borderRadius: '4px',
  display: 'block',
  margin: '0 auto',
});

// ============================================
// Pagination
// ============================================
export const PaginationContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  width: '100%',
  paddingTop: '8px',
});

export const PageButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  minWidth: '40px',
  width: '40px',
  height: '40px',
  padding: '10px',
  fontSize: '16px',
  fontWeight: 500,
  color: isActive ? '#fff' : '#262626',
  backgroundColor: isActive ? '#58b9c1' : '#fff',
  border: isActive ? 'none' : '1px solid #d9d9d9',
  borderRadius: '4px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: isActive ? '#4aa8b0' : '#f5f5f5',
  },
}));

export const NavButton = styled(Button)({
  minWidth: '40px',
  width: '40px',
  height: '40px',
  padding: '10px',
  backgroundColor: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  color: '#262626',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
});

export const EllipsisText = styled(Typography)({
  fontSize: '16px',
  color: '#737373',
  padding: '0 4px',
});

// ============================================
// 발송내용 축약 (#3) — 2줄 클램프, 전체는 hover tooltip
// ============================================
export const TruncatedContent = styled(Box)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  wordBreak: 'break-all',
  maxWidth: '360px',
  lineHeight: 1.4,
  cursor: 'default',
});

// ============================================
// 수신자 목록 (#4) — 복수 발송 시 클릭 팝오버
// ============================================
export const ReceiverCountButton = styled(Button)({
  minWidth: 'auto',
  height: '32px',
  padding: '4px 10px',
  gap: '2px',
  fontSize: '15px',
  fontWeight: 600,
  color: '#4ab3bc',
  border: '1px solid #4ab3bc',
  borderRadius: '16px',
  backgroundColor: '#f0fafb',
  textTransform: 'none',
  lineHeight: 1,
  '&:hover': {
    backgroundColor: '#e0f4f6',
  },
});

export const RecipientPopoverContent = styled(Box)({
  minWidth: '240px',
  maxWidth: '320px',
});

export const RecipientSummary = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderBottom: '1px solid #ececec',
  fontSize: '14px',
  fontWeight: 600,
});

export const RecipientSummaryItem = styled('span', {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => {
  let color = '#737373';
  if (tone === 'sent') color = '#2e9d5b';
  else if (tone === 'failed') color = '#d9482f';
  return { color, whiteSpace: 'nowrap' };
});

export const RecipientScroll = styled(Box)({
  maxHeight: '320px',
  overflowY: 'auto',
});

export const RecipientItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '10px 16px',
  borderBottom: '1px solid #f4f4f4',
  '&:last-of-type': {
    borderBottom: 'none',
  },
});

export const RecipientPhoneText = styled(Typography)({
  fontSize: '15px',
  color: '#262626',
  fontVariantNumeric: 'tabular-nums',
});

export const RecipientBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => {
  let color = '#737373';
  let backgroundColor = '#f0f0f0';
  if (status === 'SENT') {
    color = '#2e9d5b';
    backgroundColor = '#eaf8ef';
  } else if (status === 'FAILED') {
    color = '#d9482f';
    backgroundColor = '#fdece8';
  }
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color,
    backgroundColor,
    whiteSpace: 'nowrap',
  };
});
